import { useState, useMemo, useCallback } from 'react'

const STEP = 1

export function usePagination<T>(data: T[], initialLimit = 10, initialPage = 0) {
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialLimit)

  const from = useMemo(() => page * limit, [page, limit])
  const to = useMemo(() => from + limit, [from, limit])

  const lastPage = useMemo(() => {
    return Math.ceil(data.length / limit) - 1
  }, [data.length, limit])

  const returnedData = useMemo(() => {
    return data.slice(from, to)
  }, [from, to, data])

  const onNextPage = useCallback(() => {
    if (page === lastPage) {
      return
    }
    setPage((prev) => prev + STEP)
  }, [setPage, lastPage, page])

  const onPrevPage = useCallback(() => {
    if (page === 0) {
      return
    }
    setPage((prev) => prev - STEP)
  }, [setPage, page])

  const onFirstPage = useCallback(() => {
    setPage(0)
  }, [setPage])

  const onLastPage = useCallback(() => {
    setPage(lastPage)
  }, [setPage, lastPage])

  const onLimitChange = useCallback(
    (_limit: number) => {
      setLimit(_limit)
    },
    [setLimit],
  )

  return {
    limit,
    onNextPage,
    onPrevPage,
    onFirstPage,
    onLastPage,
    onLimitChange,
    page: page + 1,
    data: returnedData,
    lastPage: lastPage + 1,
  }
}
